/**
 * Client Hints for the User-Agent Parser (B-0173). A user-agent string cannot tell Windows 10 from 11 and Chrome freezes parts of it;
 * a browser's own navigator.userAgentData can. Everything here is pure and on-device: no network, nothing stored.
 */

export interface HighEntropyData {
  brands?: { brand: string; version: string }[];
  fullVersionList?: { brand: string; version: string }[];
  mobile?: boolean;
  platform?: string;
  platformVersion?: string;
  model?: string;
  architecture?: string;
  bitness?: string;
  uaFullVersion?: string;
}

export interface ClientHintsResult {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  deviceType: string;
  model: string;
  cpu: string;
}

const GREASE = /not.{0,2}a.{0,2}brand|^\W*not\W/i;
const PREFERRED = ["Microsoft Edge", "Google Chrome", "Opera", "Brave", "Vivaldi", "Samsung Internet", "Chromium"];

/** The product name and full version to show: the real browser, not Chromium or the deliberately odd "Not A;Brand" entry. */
export function pickBrand(list: { brand: string; version: string }[] | undefined): { brand: string; version: string } | null {
  const real = (list ?? []).filter((b) => b.brand && !GREASE.test(b.brand));
  for (const name of PREFERRED) { const hit = real.find((b) => b.brand === name); if (hit) return hit; }
  return real[0] ?? null;
}

/** Windows reports 13.0.0 or higher in platformVersion for Windows 11, and 1 to 12 for Windows 10. */
export function windowsFromPlatformVersion(platformVersion: string | undefined): string {
  const major = parseInt((platformVersion ?? "").split(".")[0], 10);
  if (!Number.isFinite(major)) return "Windows";
  if (major >= 13) return "Windows 11";
  if (major >= 1) return "Windows 10";
  return "Windows 7 / 8";
}

export function describeClientHints(d: HighEntropyData): ClientHintsResult {
  const brand = pickBrand(d.fullVersionList?.length ? d.fullVersionList : d.brands);
  const platform = (d.platform ?? "").replace(/^"|"$/g, "");
  const pv = (d.platformVersion ?? "").replace(/^"|"$/g, "");
  const isWindows = /^windows$/i.test(platform);
  const os = isWindows ? windowsFromPlatformVersion(pv) : platform || "Unknown";
  const model = (d.model ?? "").replace(/^"|"$/g, "");
  const arch = [d.architecture, d.bitness ? `${d.bitness}-bit` : ""].filter(Boolean).join(" ");
  return {
    browser: brand?.brand ?? "Unknown",
    browserVersion: brand?.version ?? d.uaFullVersion ?? "Unknown",
    os,
    osVersion: isWindows ? "" : pv,
    deviceType: d.mobile ? "Mobile" : "Desktop / tablet",
    model,
    cpu: arch,
  };
}

/** Structured-header list: "Chromium";v="124", "Google Chrome";v="124" -> [{brand, version}]. */
function parseBrandList(value: string): { brand: string; version: string }[] {
  const out: { brand: string; version: string }[] = [];
  const re = /"([^"]*)"\s*;\s*v\s*=\s*"([^"]*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(value))) out.push({ brand: m[1], version: m[2] });
  return out;
}

/** True when the pasted text looks like Sec-CH-UA request headers (one or more "Sec-CH-UA...: value" lines). */
export function looksLikeClientHintHeaders(text: string): boolean {
  return /^\s*sec-ch-ua[\w-]*\s*:/im.test(text);
}

/** Parses pasted Sec-CH-UA* request headers (as copied from browser dev tools or a server log) into the same shape as navigator.userAgentData. */
export function parseClientHintHeaders(text: string): HighEntropyData | null {
  const get = (name: string) => {
    const m = new RegExp(`^\\s*${name}\\s*:\\s*(.*)$`, "im").exec(text);
    return m ? m[1].trim() : undefined;
  };
  const brands = get("sec-ch-ua");
  const full = get("sec-ch-ua-full-version-list");
  const platform = get("sec-ch-ua-platform");
  if (brands === undefined && full === undefined && platform === undefined) return null;
  const unq = (v: string | undefined) => (v === undefined ? undefined : v.replace(/^"|"$/g, ""));
  const mobile = get("sec-ch-ua-mobile");
  return {
    brands: brands ? parseBrandList(brands) : undefined,
    fullVersionList: full ? parseBrandList(full) : undefined,
    platform: unq(platform),
    platformVersion: unq(get("sec-ch-ua-platform-version")),
    model: unq(get("sec-ch-ua-model")),
    architecture: unq(get("sec-ch-ua-arch")),
    bitness: unq(get("sec-ch-ua-bitness")),
    uaFullVersion: unq(get("sec-ch-ua-full-version")),
    mobile: mobile === undefined ? undefined : mobile.includes("?1"),
  };
}

export const NOT_A_UA_HINT =
  "This doesn't look like a user-agent string. A real one starts like Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 ... Copy it from a server log's user-agent field, a bug report or your browser's Network tab. Pasted Sec-CH-UA headers work too.";

export const NO_CLIENT_HINTS_NOTE =
  "This browser doesn't offer Client Hints (Chrome, Edge and Opera do; Firefox and Safari don't), so only the user-agent string is used.";
